import cn from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { MDF_ACTIVITIES_TRANSLATION_KEYS } from 'partner360/features/mdfActivities/configs/translations'
import { formatMdfContraPercent } from 'partner360/features/mdfActivities/lib/formatMdfContraPercent'
import { getMdfContraPercent } from 'partner360/features/mdfActivities/lib/getMdfContraMetrics'
import {
  IMdfActivityWithBusinessPriorities,
  TMdfBreakdownGroupField,
} from 'partner360/features/mdfActivities/model/types'
import {
  formatMdfChartValue,
  MDF_PAID_COLOR,
  MDF_PAID_SERIES,
  MDF_REQUESTED_COLOR,
  MDF_REQUESTED_SERIES,
  MdfBreakdownChart,
} from 'partner360/features/mdfActivities/ui/mdfBreakdownChart'

import type { ICockpitExportCell, ICockpitExportChart, ICockpitExportView } from '../../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  COCKPIT_MDF_GROUP_OPTIONS,
  DEFAULT_CURRENCY,
  EMPTY_GROUP_LABEL,
  formatCockpitMoney,
  getCockpitMdfGroups,
  type ICockpitMdfGroup,
  type ICockpitMdfRow,
} from '../../../../lib'
import {
  type TCockpitExportSlot,
  useFillCockpitExportSlot,
} from '../../../components/cockpitExport'
import { CockpitTableRowTitle } from '../../../components/cockpitTableTitle'
import { COCKPIT_GROUP_BY_LABEL, CockpitTileControls } from '../cockpitTileControls'
import {
  CockpitTileGroupList,
  type ICockpitTileGroupColumn,
  type ICockpitTileGroupSummaryColumn,
} from '../cockpitTileGroupList'

import { COCKPIT_MDF_CHART_TOP_GROUPS_COUNT } from './config'
import { getCockpitMdfChartGroups, type TMdfChartGroup } from './getCockpitMdfChartGroups'

import styles from './CockpitMdfDetail.module.scss'

type TFormatMoney = (value: number | null) => string

const MDF_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf

/** A totals-strip label for the deck: the strip's own translated phrase, without its markup and
 *  without the `{value}` it frames — the figure travels as the metric's value. */
const toTotalLabel = (phrase: string) =>
  phrase
    .replace(/<[^>]*>/g, '')
    .replace('{value}', '')
    .trim()

/** One activity's cells, written once for the table that shows them and for the deck that
 *  repeats them. `text` is what the user reads; money keeps its number for the slide to chart. */
const getRowCells = (row: ICockpitMdfRow, formatMoney: TFormatMoney) => ({
  name: { text: row.name },
  activityType: { text: row.activityType },
  timeframe: { text: row.timeframe },
  status: { text: row.status },
  strategicInitiatives: { text: row.strategicInitiatives.join(', ') || EMPTY_GROUP_LABEL },
  businessUnit: { text: row.businessUnit.join(', ') || EMPTY_GROUP_LABEL },
  category: { text: row.category.join(', ') || EMPTY_GROUP_LABEL },
  businessPriorities: { text: row.businessPriorities.join(', ') || EMPTY_GROUP_LABEL },
  requested: { text: formatMoney(row.requested), value: row.requested },
  paid: { text: formatMoney(row.paid), value: row.paid },
})

type TMdfColumnKey = keyof ReturnType<typeof getRowCells>

/** Widths add up to 100%, so every group's table lays out identically. */
const COLUMNS: (ICockpitTileGroupColumn & { key: TMdfColumnKey })[] = [
  { key: 'name', label: 'Name', width: '22%' },
  { key: 'activityType', label: 'Activity Type', width: '11%' },
  { key: 'timeframe', label: 'Timeframe', width: '7%' },
  { key: 'status', label: 'Status', width: '10%' },
  { key: 'strategicInitiatives', label: 'Strategic Initiative', width: '12%' },
  { key: 'businessUnit', label: 'Business Unit', width: '9%' },
  { key: 'category', label: 'Category', width: '9%' },
  { key: 'businessPriorities', label: 'GTM Business Priority', width: '10%' },
  { key: 'requested', label: 'Requested', isNumeric: true, width: '5%' },
  { key: 'paid', label: 'Paid', isNumeric: true, width: '5%' },
]

/** The bars as `MdfBreakdownChart` plots them: Paid in full, Requested only as the part not yet
 *  paid, so the bar stands at `max(requested, paid)` rather than at their sum. */
const getExportChart = (
  groups: TMdfChartGroup[],
  isUsd: boolean,
  caption: string | undefined
): ICockpitExportChart => {
  const format = (value: number) => formatMdfChartValue(value, isUsd)

  return {
    type: 'stackedBar',
    caption,
    categories: groups.map(({ name }) => name),
    series: [
      {
        name: MDF_PAID_SERIES,
        color: MDF_PAID_COLOR,
        values: groups.map(({ paid }) => paid),
        texts: groups.map(({ paid }) => format(paid)),
      },
      {
        name: MDF_REQUESTED_SERIES,
        color: MDF_REQUESTED_COLOR,
        values: groups.map(({ requested }) => requested),
        texts: groups.map(({ requested }) => format(requested)),
        stackValues: groups.map(({ requested, paid }) => Math.max(requested - paid, 0)),
      },
    ],
  }
}

const getGroupSummary = (group: ICockpitMdfGroup, formatMoney: TFormatMoney) =>
  `Requested ${formatMoney(group.requested)} · Paid ${formatMoney(group.paid)}`

interface ICockpitMdfDetailProps {
  activities: IMdfActivityWithBusinessPriorities[]
  currency: string
  isUsd: boolean
  /** HP investment the initiatives funded from MDF declare — the planned side of the three
   *  figures, which lives on the initiative rather than on any activity. */
  planned: number | null
  exportSlot?: TCockpitExportSlot
  showActivityDetails?: boolean
  onActivityClick?: (activityId: number) => void
  /** Sales of the selected partner scope in USD; undefined omits Sales and Contra columns. */
  salesUsd?: number | null
}

/**
 * The MDF sub-tab: the partner's MDF activities bucketed the same way the MDF dashboard's
 * breakdown chart buckets them, with the activities behind each bucket one click away.
 *
 * Planned money is stated once, in the header, rather than as a third column of the list: it has
 * no per-activity counterpart in the payload — an activity carries what was requested and what
 * was paid, while the planned spend is declared on the initiatives funding it.
 */
export const CockpitMdfDetail = ({
  activities,
  currency,
  isUsd,
  planned,
  exportSlot,
  showActivityDetails = true,
  onActivityClick,
  salesUsd,
}: ICockpitMdfDetailProps) => {
  const translate = useSafetyTranslation()
  const isTranslationMode = useSelector(selectTranslationMode) === TranslationMode.Edit
  const [groupField, setGroupField] = useState<TMdfBreakdownGroupField>('strategicInitiatives')
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  const [isChartExpanded, setIsChartExpanded] = useState(false)

  const groups = useMemo(
    () => getCockpitMdfGroups(activities, groupField, isUsd),
    [activities, groupField, isUsd]
  )
  const chartGroups = useMemo(
    () =>
      getCockpitMdfChartGroups(
        groups,
        translate(FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.chartOther)
      ),
    [groups, translate]
  )
  const isChartLimited = groups.length > COCKPIT_MDF_CHART_TOP_GROUPS_COUNT
  const chartToggleTranslationKey = isChartExpanded
    ? FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.hideChart
    : FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.showChart
  const selectedGroupName =
    COCKPIT_MDF_GROUP_OPTIONS.find(({ value }) => value === groupField)?.label ?? groupField
  const showSalesAndContra = salesUsd !== undefined
  const summaryColumns = useMemo<ICockpitTileGroupSummaryColumn[]>(
    () => [
      {
        key: 'requested',
        label: (
          <Translation
            span
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.table.requested}
          />
        ),
        isNumeric: true,
      },
      {
        key: 'paid',
        label: (
          <Translation
            span
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.table.paid}
          />
        ),
        isNumeric: true,
      },
      ...(showSalesAndContra
        ? [
            {
              key: 'sales',
              label: (
                <Translation
                  span
                  text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.table.sales}
                />
              ),
              isNumeric: true,
            },
            {
              key: 'contra',
              label: (
                <span className={styles.contraLabel}>
                  <Translation
                    span
                    text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.table.contra}
                  />
                  <Tooltip
                    inline
                    tipContent={() => (
                      <Translation
                        span
                        text={MDF_ACTIVITIES_TRANSLATION_KEYS.dashboard.overview.contra.tooltip}
                      />
                    )}
                  >
                    <i
                      className={cn('kbase kbase-info', styles.contraInfoIcon)}
                      aria-hidden="true"
                    />
                  </Tooltip>
                </span>
              ),
              isNumeric: true,
            },
          ]
        : []),
      {
        key: 'activities',
        label: (
          <Translation
            span
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.table.activities}
          />
        ),
        isNumeric: true,
      },
    ],
    [showSalesAndContra]
  )
  const paidUsdByGroup = useMemo(
    () =>
      showSalesAndContra
        ? new Map(
            getCockpitMdfGroups(activities, groupField, true).map((group) => [
              group.name,
              group.paid,
            ])
          )
        : new Map<string, number>(),
    [activities, groupField, showSalesAndContra]
  )

  /** Read off the buckets rather than the activities, so the header can never disagree with
   *  what the chart below it draws. */
  const totals = useMemo(
    () =>
      groups.reduce(
        (sum, group) => ({
          requested: sum.requested + group.requested,
          paid: sum.paid + group.paid,
        }),
        { requested: 0, paid: 0 }
      ),
    [groups]
  )

  const formatMoney = useCallback<TFormatMoney>(
    (value) => formatCockpitMoney(value, currency),
    [currency]
  )

  /** One group's row under the list's own header, written once for the list and for the deck.
   *  Sales covers the selected partners, the same on every row, and Contra sets the group's paid money against
   *  it — both in USD, whatever currency the rest reads in. */
  const getGroupCells = useCallback(
    (group: ICockpitMdfGroup): Record<string, ICockpitExportCell> => {
      const contra = getMdfContraPercent(paidUsdByGroup.get(group.name) ?? 0, salesUsd ?? null)

      return {
        requested: { text: formatMoney(group.requested), value: group.requested },
        paid: { text: formatMoney(group.paid), value: group.paid },
        ...(showSalesAndContra
          ? {
              sales: {
                text: formatCockpitMoney(salesUsd ?? null, DEFAULT_CURRENCY),
                value: salesUsd ?? null,
              },
              contra: { text: formatMdfContraPercent(contra), value: contra },
            }
          : {}),
        activities: { text: String(group.rows.length), value: group.rows.length },
      }
    },
    [formatMoney, paidUsdByGroup, salesUsd, showSalesAndContra]
  )

  const handleSelectGroup = (value: TMdfBreakdownGroupField) => {
    setGroupField(value)
    setExpandedGroups([])
  }

  const handleToggleGroup = (name: string) => {
    setExpandedGroups((expanded) =>
      expanded.includes(name) ? expanded.filter((group) => group !== name) : [...expanded, name]
    )
  }

  /** The buckets as they stand, carrying activities only where the user opened one. The chart
   *  travels only while it is open, as the tile draws it only then. */
  const exportView = useCallback((): ICockpitExportView => {
    const chartCaption = isChartLimited
      ? translate(MDF_KEYS.chartLimitSummary)
          .replaceAll('{topCount}', String(COCKPIT_MDF_CHART_TOP_GROUPS_COUNT))
          .replaceAll('{totalCount}', String(groups.length))
          .replaceAll('{groupName}', selectedGroupName)
      : undefined

    return {
      key: 'mdf',
      controls: [
        {
          label: COCKPIT_GROUP_BY_LABEL,
          value: COCKPIT_MDF_GROUP_OPTIONS.find(({ value }) => value === groupField)?.label ?? '',
          options: COCKPIT_MDF_GROUP_OPTIONS.map(({ label }) => label),
        },
      ],
      metrics: [
        { label: toTotalLabel(translate(MDF_KEYS.totals.planned)), value: formatMoney(planned) },
        {
          label: toTotalLabel(translate(MDF_KEYS.totals.requested)),
          value: formatMoney(totals.requested),
        },
        { label: toTotalLabel(translate(MDF_KEYS.totals.paid)), value: formatMoney(totals.paid) },
        { label: 'Activities', value: String(activities.length) },
      ],
      chart:
        isChartExpanded && chartGroups.length
          ? getExportChart(chartGroups, isUsd, chartCaption)
          : undefined,
      table: {
        columns: COLUMNS.map(({ key, label, isNumeric }) => ({ key, title: label, isNumeric })),
        groupColumns: [
          { key: 'name', title: selectedGroupName },
          ...summaryColumns.map(({ key, isNumeric }) => ({
            key,
            title: translate(MDF_KEYS.table[key as keyof typeof MDF_KEYS.table]),
            isNumeric,
          })),
        ],
        groups: groups.map((group) => {
          const isExpanded = showActivityDetails && expandedGroups.includes(group.name)

          return {
            name: group.name,
            count: group.rows.length,
            summary: getGroupSummary(group, formatMoney),
            isExpanded,
            cells: { name: { text: group.name }, ...getGroupCells(group) },
            rows: isExpanded
              ? group.rows.map((row) => ({
                  id: String(row.activityId),
                  cells: getRowCells(row, formatMoney) as Record<string, ICockpitExportCell>,
                }))
              : undefined,
          }
        }),
      },
    }
  }, [
    groupField,
    formatMoney,
    planned,
    totals,
    activities.length,
    chartGroups,
    groups,
    expandedGroups,
    isUsd,
    showActivityDetails,
    isChartExpanded,
    isChartLimited,
    selectedGroupName,
    summaryColumns,
    getGroupCells,
    translate,
  ])

  useFillCockpitExportSlot(exportSlot, exportView)

  return (
    <div className={styles.root}>
      <div className={styles.totals}>
        <span className={styles.total}>
          <Translation
            span
            htmlAvailable
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.totals.planned}
            replaceParams={[{ key: '{value}', value: formatMoney(planned) }]}
          />
        </span>
        <span className={styles.total}>
          <Translation
            span
            htmlAvailable
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.totals.requested}
            replaceParams={[{ key: '{value}', value: formatMoney(totals.requested) }]}
          />
        </span>
        <span className={styles.total}>
          <Translation
            span
            htmlAvailable
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.totals.paid}
            replaceParams={[{ key: '{value}', value: formatMoney(totals.paid) }]}
          />
        </span>
        <span className={styles.count}>{activities.length} activities</span>
      </div>

      <CockpitTileControls
        groupOptions={COCKPIT_MDF_GROUP_OPTIONS}
        selectedGroup={groupField}
        onSelectGroup={handleSelectGroup}
        actions={
          groups.length ? (
            <div className={styles.chartActions}>
              {isChartExpanded && isChartLimited && (
                <span className={styles.chartLimitSummary}>
                  {isTranslationMode && (
                    <Translation
                      span
                      text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.chartOther}
                    />
                  )}
                  <Translation
                    span
                    text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.chartLimitSummary}
                    replaceParams={[
                      {
                        key: '{topCount}',
                        value: String(COCKPIT_MDF_CHART_TOP_GROUPS_COUNT),
                      },
                      { key: '{totalCount}', value: String(groups.length) },
                      { key: '{groupName}', value: selectedGroupName },
                    ]}
                  />
                </span>
              )}

              <Tooltip
                inline
                tipContent={() => <Translation span text={chartToggleTranslationKey} />}
              >
                <Button
                  variant="text"
                  size="sm"
                  icon="kbase kbase-chart"
                  onClick={() => setIsChartExpanded((expanded) => !expanded)}
                >
                  <span className={styles.visuallyHidden}>
                    {translate(chartToggleTranslationKey)}
                  </span>
                  <i
                    className={`kbase ${
                      isChartExpanded ? 'kbase-chevron-top' : 'kbase-chevron-bottom'
                    }`}
                    aria-hidden="true"
                  />
                </Button>
              </Tooltip>
            </div>
          ) : null
        }
      />

      {isChartExpanded && groups.length ? (
        <MdfBreakdownChart groups={chartGroups} isUsd={isUsd} />
      ) : !groups.length ? (
        <div className={styles.empty}>No MDF activities for this grouping.</div>
      ) : null}

      <CockpitTileGroupList
        columns={COLUMNS}
        groupLabel={selectedGroupName}
        summaryColumns={summaryColumns}
        expandedGroups={expandedGroups}
        onToggleGroup={handleToggleGroup}
        expandable={showActivityDetails}
        groups={groups.map((group) => ({
          name: group.name,
          count: group.rows.length,
          summaryCells: Object.fromEntries(
            Object.entries(getGroupCells(group)).map(([key, { text }]) => [key, text])
          ),
          rows: group.rows.map((row) => {
            const cells = getRowCells(row, formatMoney)

            return (
              <tr key={`${group.name}-${row.activityId}`}>
                {COLUMNS.map(({ key, isNumeric }) => (
                  <td key={key} className={cn({ [styles.numeric]: isNumeric })}>
                    {key === 'name' ? (
                      <CockpitTableRowTitle>
                        {onActivityClick ? (
                          <button
                            type="button"
                            className={styles.activityLink}
                            onClick={() => onActivityClick(row.activityId)}
                          >
                            {cells[key].text}
                          </button>
                        ) : (
                          cells[key].text
                        )}
                      </CockpitTableRowTitle>
                    ) : (
                      cells[key].text
                    )}
                  </td>
                ))}
              </tr>
            )
          }),
        }))}
      />
    </div>
  )
}

CockpitMdfDetail.displayName = 'CockpitMdfDetail'
